import React, {
  ReactNode,
  FC,
  Dispatch,
  createContext,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { Typography } from '@mui/material';
import { version } from './config/version';
import { hasDefinedProperties } from '@shared/helpers/object.helpers';
import { resolvePrintSessionApiUrl } from '@shared/helpers/report-pdf-localhost.helpers';
import {
  DEFAULT_SERVER_LISTEN_PORT,
  devLoopbackApiUrl,
  parseListenPort,
} from '@shared/helpers/listen-port.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  NodonEnvironment,
  ServerEnvironment,
} from '@shared/models/environment.model';
import { GlobalLoader } from '@ui/GlobalLoader';

type IAuth0Config = {
  domain: ServerEnvironment['AUTH0_DOMAIN'];
  clientId: ServerEnvironment['AUTH0_CLIENT_ID'];
  audience: ServerEnvironment['AUTH0_AUDIENCE'];
};

/**
 * Config for client only.
 * If changes are made remember to update the config in google cloud secret manager
 */
export type IConfig = {
  loading: boolean;
  loaded: boolean;
  error?: Error;
  auth0: IAuth0Config;
  /**
   * Url to API
   */
  apiUrl: string;

  mapboxAccessToken?: string;

  environment: ServerEnvironment['ENVIRONMENT'];
  version: string;
  maintenanceMode?: boolean;
  amplitudeApiKey?: string;
};

/**
 * Default config that loads values from your .env locally.
 * Is overridden by config.json from google cloud secret manager on test/prod
 * You can also edit the public/config.json file to change config locally
 */
const DEFAULT_CONFIG: IConfig = {
  loading: true,
  loaded: false,
  apiUrl: 'http://localhost:50000',
  mapboxAccessToken:
    'pk.eyJ1IjoiZXJpY2phY29ic3NvbiIsImEiOiJjbWhzenBhaG0xZDFlMmlzNGI5aGZxeGo2In0.BvyWJzHrpI2e2z_anJF_RA',
  auth0: {
    domain: 'nodon.eu.auth0.com',
    clientId: 'yTF7Jv9u1uXwQJ0q0WOpc5BkjVLHLAsW',
    audience: 'nodon-api-test',
  },
  version: version ?? '0.0.0',
  environment: 'localhost',
};

export type IPartialConfig = Partial<Omit<IConfig, 'auth0'>> & {
  auth0?: Partial<IAuth0Config>;
};

export function mergeConfigs(a: IConfig, b: IPartialConfig): IConfig {
  return { ...a, ...b, auth0: { ...a.auth0, ...b.auth0 } };
}

export type Action =
  | { type: 'set config'; config: IPartialConfig }
  | { type: 'error'; error?: Error };

const reducer = (config: IConfig, action: Action): IConfig => {
  switch (action.type) {
    case 'set config': {
      return {
        ...mergeConfigs(config, action.config),
        loaded: true,
        loading: false,
      };
    }
    case 'error': {
      return { ...config, error: action.error, loading: false };
    }
    default: {
      console.error('invalid dispatch action: ', action);
      return config;
    }
  }
};

const ConfigStateContext = createContext<IConfig | undefined>(undefined);
const ConfigDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined,
);

type IConfigProviderProps = {
  children: ReactNode;
};

export const ConfigProvider: FC<IConfigProviderProps> = ({ children }) => {
  const [config, dispatch] = useReducer(reducer, DEFAULT_CONFIG);

  useEffect(() => {
    void dispatchConfig(dispatch);
  }, []);

  return (
    <ConfigStateContext.Provider value={config}>
      <ConfigDispatchContext.Provider value={dispatch}>
        {config.error && (
          <>
            <Typography variant="h6" color="error">
              Unable to load configuration
            </Typography>
            <Typography variant="subtitle1">{config.error.message}</Typography>
          </>
        )}
        <GlobalLoader.Register open={config.loading} text="Loading Nodon" />
        {config.loaded && children}
      </ConfigDispatchContext.Provider>
    </ConfigStateContext.Provider>
  );
};

export const useConfig = (): [IConfig, Dispatch<Action>] => {
  const config = useContext(ConfigStateContext);
  const dispatch = useContext(ConfigDispatchContext);

  if (config === undefined || dispatch === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }

  return [config, dispatch];
};

export const useAppVersion = (): string => {
  const [config] = useConfig();
  return config.version;
};

const dispatchConfig = async (
  dispatch: Dispatch<Action>,
  configPath?: string,
): Promise<void> => {
  try {
    const config = await getConfig(configPath);
    dispatch({
      type: 'set config',
      config,
    });
  } catch (e: any) {
    dispatch({ type: 'error', error: e });
  }
};

const configRecord: Record<string, IConfig> = {};

/**
 * API url override that lets e2e tests target a dedicated API instance.
 * Honored on a local dev server only — a built bundle must always use the
 * apiUrl from config.json, so it can never be repointed at another API.
 */
const getApiUrlOverride = (): string | undefined => {
  const override = import.meta.env.VITE_API_URL;
  if (!override) return undefined;
  if (import.meta.env.DEV) return override;

  console.error(
    'VITE_API_URL ignored — the API url can only be overridden on a local dev server',
  );
  return undefined;
};

/**
 * Appends a cache-busting query so leftover Google Frontend entries are not reused.
 */
const withCacheBustQuery = (path: string): string =>
  `${path}${path.includes('?') ? '&' : '?'}r=${Date.now()}`;

const CONFIG_ENVIRONMENTS: readonly IConfig['environment'][] = [
  NodonEnvironment.Localhost,
  NodonEnvironment.Development,
  NodonEnvironment.Production,
];

const AUTH0_AUDIENCES: readonly IAuth0Config['audience'][] = [
  'nodon-api-test',
  'nodon-api-prod',
];

/** True when `value` is a known {@link IConfig} environment string. */
const isConfigEnvironment = (value: unknown): value is IConfig['environment'] =>
  CONFIG_ENVIRONMENTS.some((environment) => environment === value);

/** True when `value` is the Auth0 domain used by Nodon. */
const isAuth0Domain = (value: unknown): value is IAuth0Config['domain'] =>
  value === DEFAULT_CONFIG.auth0.domain;

/** True when `value` is a known Auth0 API audience. */
const isAuth0Audience = (value: unknown): value is IAuth0Config['audience'] =>
  AUTH0_AUDIENCES.some((audience) => audience === value);

/**
 * Returns `undefined` when the field is absent so merge can keep defaults.
 * Throws when the field is present but fails `isValid`.
 */
function readOptionalField<T>(
  value: unknown,
  field: string,
  isValid: (value: unknown) => value is T,
): T | undefined {
  if (guards.is.undefined(value)) return undefined;
  if (!isValid(value)) {
    throw new Error(`Invalid configuration field: ${field}`);
  }
  return value;
}

/**
 * Reads optional Auth0 fields from `config.json`. Throws when `auth0` is present but not an object.
 */
const parseAuth0Config = (auth0: unknown): IPartialConfig['auth0'] => {
  if (guards.is.undefined(auth0)) return undefined;
  if (!guards.is.object(auth0)) {
    throw new Error('Invalid configuration field: auth0');
  }

  const domain = readOptionalField(auth0.domain, 'auth0.domain', isAuth0Domain);
  const clientId = readOptionalField(
    auth0.clientId,
    'auth0.clientId',
    guards.is.string,
  );
  const audience = readOptionalField(
    auth0.audience,
    'auth0.audience',
    isAuth0Audience,
  );

  return {
    ...(guards.is.undefined(domain) ? {} : { domain }),
    ...(guards.is.undefined(clientId) ? {} : { clientId }),
    ...(guards.is.undefined(audience) ? {} : { audience }),
  };
};

/**
 * Reads `config.json` as a partial config. Rejects non-object JSON at the boundary.
 * Present fields with the wrong shape throw rather than being omitted.
 */
const parseConfigJson = (value: unknown): IPartialConfig => {
  if (!guards.is.object(value)) {
    throw new Error('Config is missing required fields');
  }

  const apiUrl = readOptionalField(value.apiUrl, 'apiUrl', guards.is.string);
  const mapboxAccessToken = readOptionalField(
    value.mapboxAccessToken,
    'mapboxAccessToken',
    guards.is.string,
  );
  const environment = readOptionalField(
    value.environment,
    'environment',
    isConfigEnvironment,
  );
  const configVersion = readOptionalField(
    value.version,
    'version',
    guards.is.string,
  );
  const maintenanceMode = readOptionalField(
    value.maintenanceMode,
    'maintenanceMode',
    guards.is.boolean,
  );
  const amplitudeApiKey = readOptionalField(
    value.amplitudeApiKey,
    'amplitudeApiKey',
    guards.is.string,
  );
  const auth0 = parseAuth0Config(value.auth0);

  return {
    ...(guards.is.undefined(apiUrl) ? {} : { apiUrl }),
    ...(guards.is.undefined(mapboxAccessToken) ? {} : { mapboxAccessToken }),
    ...(guards.is.undefined(environment) ? {} : { environment }),
    ...(guards.is.undefined(configVersion) ? {} : { version: configVersion }),
    ...(guards.is.undefined(maintenanceMode) ? {} : { maintenanceMode }),
    ...(guards.is.undefined(amplitudeApiKey) ? {} : { amplitudeApiKey }),
    ...(guards.is.undefined(auth0) ? {} : { auth0 }),
  };
};

/**
 * Get config from client-server. Fetch only once per path, then cache it in memory.
 * Config is handled in google cloud secret manager (https://console.cloud.google.com/security/secret-manager)
 * And will automatically be added to build.
 * Uses fetch `cache: 'no-store'` so the browser does not reuse a cached `config.json` after a deploy.
 */
export const getConfig = async (
  path = '/config/config.json',
): Promise<IConfig> => {
  if (!configRecord[path]) {
    const response = await fetch(withCacheBustQuery(path), {
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(`Failed to load configuration (${response.status})`);
    }

    const config = mergeConfigs(
      DEFAULT_CONFIG,
      parseConfigJson(await response.json()),
    );

    const apiUrlOverride = getApiUrlOverride();
    if (apiUrlOverride) {
      config.apiUrl = apiUrlOverride;
    } else {
      const apiPort = import.meta.env.VITE_API_PORT;
      if (
        import.meta.env.DEV &&
        apiPort &&
        config.environment === NodonEnvironment.Localhost
      ) {
        config.apiUrl = devLoopbackApiUrl(
          parseListenPort(apiPort, DEFAULT_SERVER_LISTEN_PORT),
          typeof window === 'undefined' ? undefined : window.location.hostname,
        );
      }
      config.apiUrl = resolvePrintSessionApiUrl({
        apiUrl: config.apiUrl,
        pageHref:
          typeof window === 'undefined' ? undefined : window.location.href,
        isDev: import.meta.env.DEV,
      });
    }

    validateConfig(config);
    configRecord[path] = config;
  }

  return configRecord[path];
};

/**
 * Make sure config is valid, else throw error
 * @param config
 * @returns
 */
const validateConfig = (
  config: undefined | IPartialConfig,
): config is IConfig => {
  if (!hasDefinedProperties(config, 'apiUrl', 'auth0')) {
    throw new Error('Config is missing required fields');
  }
  return true;
};
